import { Form } from "@circle-react-uikit/Form";
import { Avatar } from "@circleco/compass/components/Avatar";

import { useCreateComment } from "@/react/components/CommentsCompass/data/useCreateComment";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import type { TiptapFormValues } from "@/react/components/CommentsCompass/types";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { cssAnchorProps } from "@/react/helpers/cssAnchorHelpers";
import type { Comment } from "@/react/types/Comment";
import type { BasicCommunityMember } from "@/react/types/CommunityMember";

import { ComposerBody } from "./ComposerBody";
import { useStoredCommentDraft } from "./useCommentDraftStorage";

export interface ComposerFormProps {
  className?: string;
  isCollapsible?: boolean;
  isFocusPending: boolean;
  mentionCommunityMember?: BasicCommunityMember | null;
  onCreate?: (comment: Comment) => void;
  onFocusHandled: () => void;
  parentCommentId?: number;
  replyToCommunityMember?: BasicCommunityMember | null;
}

export const ComposerForm = ({
  className,
  isCollapsible,
  mentionCommunityMember,
  parentCommentId,
  replyToCommunityMember,
  isFocusPending,
  onCreate,
  onFocusHandled,
}: ComposerFormProps) => {
  const { onCommentCreated, postId } = useCommentsConfig();
  const { currentCommunityMember } = usePunditUserContext();

  const { storedDraft } = useStoredCommentDraft({ parentCommentId });

  const { createComment } = useCreateComment({
    postId,
    parentCommentId,
    onSuccess: (comment: Comment) => {
      onCommentCreated(comment);
      onCreate?.(comment);
    },
  });

  return (
    <div
      className={className}
      {...cssAnchorProps({ anchor: "comment-composer" })}
    >
      <div className="flex items-start gap-3">
        <Avatar
          size={parentCommentId ? "md" : "lg"}
          name={currentCommunityMember?.name ?? ""}
          src={currentCommunityMember?.avatar_url}
        />
        <Form<TiptapFormValues>
          className="relative min-w-0 flex-1"
          defaultValues={{ tiptap_body: storedDraft }}
          onSubmit={async formData => {
            await createComment(formData);
          }}
        >
          <ComposerBody
            isCollapsible={isCollapsible}
            mentionCommunityMember={mentionCommunityMember}
            parentCommentId={parentCommentId}
            replyToCommunityMember={replyToCommunityMember}
            isFocusPending={isFocusPending}
            onFocusHandled={onFocusHandled}
          />
        </Form>
      </div>
    </div>
  );
};
